/**
 * Single source of truth for all zone-property synthetic path components.
 *
 * These constants are shared between:
 *   - ZoneAnalysisReviewPanel (builds anchor property strings for CommentIndicator)
 *   - anchorValues.ts (parses those strings back to resolve current field values)
 *   - useZonePropertyAnchor / floating-card panels (direct zone-property keys)
 */

// ── Synthetic prefixes (segments[0] in review-panel anchor templates) ──

export const ZONE_SYNTHETIC_PREFIX = {
  DAMAGE_MECHANISM: 'dm',
  DETAILS: 'details',
  STRATEGY: 'strategy',
  RBA: 'rba',
  COVERAGE: 'coverage',
} as const

// ── Section-level property keys (CommentIndicator section wrappers) ────

export const ZONE_SECTION_KEYS = {
  DAMAGE_MECHANISMS: 'damage_mechanisms',
  ZONE_DETAILS: 'zone_details',
  NII_STRATEGY: 'nii_strategy',
  RBA_INFO: 'rba_info',
  COVERAGE_REQUIREMENTS: 'coverage_requirements',
  COVERAGE_BREAKDOWN: 'coverage_breakdown',
} as const

// ── Zone detail label → field mapping ──────────────────────────────────
// `field`   — direct property on ZoneParams
// `resolve` — requires a computed accessor (effectiveLength, etc.)

export const ZONE_DETAIL_FIELDS = [
  { label: 'Zone Material', field: 'material' },
  { label: 'Zone Geometry', field: 'geometry' },
  { label: 'Zone Length (mm)', resolve: 'effectiveLength' },
  { label: 'Zone Height (mm)', resolve: 'effectiveHeight' },
  { label: 'Zone Internal Diameter (mm)', field: 'internal_diameter_mm' },
  { label: 'Zone External Diameter (mm)', field: 'external_diameter_mm' },
  { label: 'Total Surface Area (sq m)', resolve: 'effectiveArea' },
  {
    label: 'Zone Wall Thickness (mm): Shell',
    field: 'wall_thickness_shell_mm',
  },
  {
    label: 'Zone Wall Thickness (mm): Heads',
    field: 'wall_thickness_heads_mm',
  },
  {
    label: 'Zone Design Corrosion Allowance (mm)',
    field: 'corrosion_allowance_mm',
  },
  { label: 'Design Pressure (bar)', field: 'design_pressure_bar' },
  { label: 'Operating Pressure (bar)', field: 'operating_pressure_bar' },
  { label: 'Design Temperature (°C)', field: 'design_temp_c' },
  { label: 'Max Operating Temperature (°C)', field: 'max_operating_temp_c' },
] as const

export type ZoneDetailField = (typeof ZONE_DETAIL_FIELDS)[number]

// ── RBA shorthand keys ─────────────────────────────────────────────────

export const RBA_KEYS = {
  DENSITY: {
    key: 'density',
    value: 'density_of_degradation',
    justification: 'density_justification',
  },
  ACCURACY: {
    key: 'accuracy',
    value: 'measurement_accuracy',
    justification: 'measurement_accuracy_justification',
  },
  TOLERANCE: {
    key: 'tolerance',
    value: 'tolerance_of_degradation',
    justification: 'tolerance_justification',
  },
  HOMOGENEITY: {
    key: 'homogeneity',
    value: 'spatial_homogeneity',
    justification: 'spatial_homogeneity_justification',
  },
} as const

export const RBA_KEY_LIST = Object.values(RBA_KEYS)

// ── Coverage column → coverage_calculation field ───────────────────────

export const COVERAGE_COLUMNS = [
  { dataIndex: 'coverageModifier', field: 'f_cov', title: 'Coverage Modifier' },
  {
    dataIndex: 'consequenceOfFailure',
    field: 'f_cons',
    title: 'Consequence of Failure',
  },
  { dataIndex: 'zoneArea', field: 'f_zone', title: 'Zone Area' },
  {
    dataIndex: 'baseCoverage',
    field: 'base_coverage_percent',
    title: 'Base Coverage',
  },
  {
    dataIndex: 'measurementAccuracy',
    field: 'f_acc',
    title: 'Measurement Accuracy',
  },
  {
    dataIndex: 'spatialHomogeneity',
    field: 'f_sh',
    title: 'Spatial Homogeneity',
  },
  {
    dataIndex: 'toleranceOfDegradation',
    field: 'f_lc',
    title: 'Tolerance of Degradation',
  },
  {
    dataIndex: 'coverageRequired',
    field: 'required_coverage_percent',
    title: 'Coverage Required (as a %)',
  },
  {
    dataIndex: 'coverageArea',
    field: 'required_coverage_area_sq_m',
    title: 'Coverage Area',
  },
] as const

// ── Strategy row keys ──────────────────────────────────────────────────

export const STRATEGY_KEYS = {
  DEGRADATION_LIKELY: 'degradation_likely',
  DEGRADATION_PREDICTABLE: 'degradation_predictable',
  DEGRADATION_SEVERITY: 'degradation_severity',
} as const

// ── DM column keys ─────────────────────────────────────────────────────

export const DAMAGE_MECHANISM_COLUMNS = {
  NAME: 'name',
  RATE: 'rate',
  JUSTIFICATION: 'justification',
} as const

// ── Overview table column → zone field mapping ─────────────────────────
// dataIndex values are camelCase for Ant Table; zone fields are snake_case.

export const OVERVIEW_COL_TO_ZONE_FIELD: Record<string, string> = {
  description: 'description',
  damageMechanisms: 'damage_mechanisms',
  coveragePercent: 'required_coverage_percent',
  coverageArea: 'required_coverage_area_sq_m',
}
